import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaArrowRight } from "react-icons/fa";
import {  FiLogOut, FiSettings, FiGrid } from "react-icons/fi";
import { Link, useNavigate, useLocation } from "react-router-dom";
import packageJson from "../../package.json";

// Animated, client-side navigation link (no full page reload).
const MotionLink = motion.create(Link);

const getAuthState = () => {
  try {
    const token = localStorage.getItem("divyayoga_token");
    const role  = localStorage.getItem("divyayoga_role");
    const user  = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
    if (token && user) return { loggedIn: true, role, user };
  } catch {}
  return { loggedIn: false, role: null, user: null };
};


const clearAuth = () => {
  localStorage.removeItem("divyayoga_token");
  localStorage.removeItem("divyayoga_user");
  localStorage.removeItem("divyayoga_role");
};

const navItems1 = [
  { name:"Home",     path:"/"         },
  { name:"About",    path:"/about-us" },
  { name:"Benefits", path:"/benefits" },
];

const navItems2 = [
  { name:"Glimpses",  path:"/gallery"  },
  // { name:"Milestones", path:"/milestones"},
  { name:"Blog", path:"/blog"},
  { name:"Contact",  path:"/contact"  },
];

const Navbar = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [dropOpen,    setDropOpen]    = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const [auth,        setAuth]        = useState(getAuthState);

  // ── Re-read auth on every route change (handles login / logout elsewhere) ──
  useEffect(() => {
    setAuth(getAuthState());
    setExploreOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // ── Lock body scroll when mobile menu is open ───────────────────────────────
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // ── Scroll listener ────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Close dropdown on outside click ───────────────────────────────────────
  useEffect(() => {
    if (!dropOpen) return;
    const close = (e) => {
      if (!e.target.closest("[data-avatar-menu]")) setDropOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [dropOpen]);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    clearAuth();
    setAuth({ loggedIn:false, role:null, user:null });
    setDropOpen(false);
    setMobileOpen(false);
    navigate("/");
  };

  // ── Avatar initials ────────────────────────────────────────────────────────
  const getInitials = (user) => {
    if (!user) return "U";
    const name = user.name || user.fullName || user.userName || user.email || "";
    return name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase() || "U";
  };

  const dashboardPath = auth.role === "Admin" ? "/admin/dashboard" : "/user/dashboard";
  const roleLabel     = auth.role === "Admin" ? "admin" : "Member";
  const userName      = auth.user?.name || auth.user?.fullName || auth.user?.userName || "Account";

  return (
    <>
      {/* ══ NAVBAR ══════════════════════════════════════════════ */}
      <motion.header
        initial={{ y:-80, opacity:0 }}
        animate={{ y:0,  opacity:1 }}
        transition={{ duration:0.7 }}
        className={`fixed top-0 left-0 w-full z-[999] transition-all duration-500 px-4 lg:px-8 pt-2 ${scrolled ? "pb-4" : "pb-0"}`}
      >
        <div className={`relative overflow-visible max-w-[1600px] mx-auto rounded-[28px]
            transition-all duration-500 border
            ${scrolled
              ? "border-[#F4D9B5] bg-white/70 backdrop-blur-3xl shadow-[0_20px_80px_rgba(0,0,0,0.06)]"
              : "border-white/10 bg-white/10 backdrop-blur-2xl"}`}>

          {/* Glow + mesh */}
          <div className="absolute top-[-120px] right-[-120px] w-[260px] h-[260px] rounded-full bg-[#F29A0C]/12 blur-[100px]"/>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.10),transparent_25%) rounded-full]"/>

          <div className="relative z-20 h-[82px] px-6 lg:px-8 flex items-center justify-between rounded-full">

            {/* LOGO */}
            <motion.div whileHover={{ scale:1.02 }} className="flex items-center gap-4 cursor-pointer" onClick={() => navigate("/")}>
              <div className="relative overflow-hidden w-14 h-14 rounded-[20px] border border-white/10 bg-white/20 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
                <img src="/assets/logo.jpeg" alt="Divyyog" className="w-full h-full object-cover"/>
              </div>
              <div className="flex flex-col justify-center">
                <h2 className={`text-[26px] leading-none tracking-tight font-extrabold transition-all duration-300
                  ${scrolled ? "text-[#111111]" : "text-white"}`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Divyyog
                </h2>
                <p className={`text-[11px] uppercase tracking-[4px] mt-1 transition-all duration-300 font-semibold
                  ${scrolled ? "text-[#666666]" : "text-white/80"}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Foundation
                </p>
              </div>
            </motion.div>

            {/* DESKTOP NAV LINKS */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Part 1: Home, About, Benefits */}
              {navItems1.map((item, i) => {
                const active = location.pathname === item.path;
                return (
                  <MotionLink key={i} to={item.path} whileHover={{ y:-2 }}
                    className="group relative overflow-hidden px-6 py-3 rounded-full transition-all duration-300">
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400]
                      transition-all duration-300
                      ${active 
                        ? "opacity-100 scale-100 shadow-[0_10px_30px_rgba(242,154,12,0.3)]" 
                        : "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100"}`}
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-1000
                      bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.25),transparent)]
                      -translate-x-full group-hover:translate-x-full skew-x-12"/>
                    <span className={`relative z-20 text-[16px] font-bold tracking-wide transition-all duration-300
                      ${active 
                        ? "text-white" 
                        : scrolled 
                          ? "text-[#111111] group-hover:text-white" 
                          : "text-white"}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {item.name}
                    </span>
                  </MotionLink>
                );
              })}

              {/* Unique Dropdown for Quick Links (placed beside Benefits) */}
              <div 
                className="relative"
                onMouseEnter={() => setExploreOpen(true)}
                onMouseLeave={() => setExploreOpen(false)}
              >
                <motion.button
                  whileHover={{ y:-2 }}
                  className="group relative overflow-hidden px-6 py-3 rounded-full transition-all duration-300 cursor-pointer border-none bg-transparent outline-none flex items-center gap-1.5"
                >
                  <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400]
                    transition-all duration-300 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100`}
                  />
                  <span className={`relative z-20 text-[16px] font-bold tracking-wide transition-all duration-300
                    ${scrolled 
                      ? "text-[#111111] group-hover:text-white" 
                      : "text-white"}`}
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    Explore
                  </span>
                  <span className={`relative z-20 text-[10px] transition-all duration-300
                    ${scrolled 
                      ? "text-[#111111] group-hover:text-white" 
                      : "text-white"}`}>
                    ▼
                  </span>
                </motion.button>

                <AnimatePresence>
                  {exploreOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 15, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 15, scale: 0.95 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-1/2 -translate-x-1/2 top-[calc(100%-2px)] pt-3 w-[260px] z-[9999]"
                    >
                      <div 
                        className="rounded-[24px] border border-[#F4D9B5] bg-[#FFFDFB] p-3.5 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
                      >
                        <ul className="space-y-1">
                          <li>
                            <Link 
                              to="/trainers"
                              className="flex flex-col px-4 py-2.5 rounded-[16px] hover:bg-[#FFF8EE] transition-colors"
                              onClick={() => setExploreOpen(false)}
                            >
                              <span className="text-[14px] font-bold text-[#111111] hover:text-[#F29A0C]">Trainers & Masters</span>
                              <span className="text-[10px] text-[#777] font-semibold mt-0.5">Living Lineage & Acharyas</span>
                            </Link>
                          </li>
                          <li>
                            <Link 
                              to="/courses"
                              className="flex flex-col px-4 py-2.5 rounded-[16px] hover:bg-[#FFF8EE] transition-colors"
                              onClick={() => setExploreOpen(false)}
                            >
                              <span className="text-[14px] font-bold text-[#111111] hover:text-[#F29A0C]">Courses</span>
                              <span className="text-[10px] text-[#777] font-semibold mt-0.5">Nurture Your Sacred Journey</span>
                            </Link>
                          </li>
                          <li>
                            <Link 
                              to="/live-classes"
                              className="flex flex-col px-4 py-2.5 rounded-[16px] hover:bg-[#FFF8EE] transition-colors"
                              onClick={() => setExploreOpen(false)}
                            >
                              <span className="text-[14px] font-bold text-[#111111] hover:text-[#F29A0C]">Live Classes</span>
                              <span className="text-[10px] text-[#777] font-semibold mt-0.5">Real-Time Resonance Flow</span>
                            </Link>
                          </li>                      
                          <li>
                            <Link 
                              to="/pricing"
                              className="flex flex-col px-4 py-2.5 rounded-[16px] hover:bg-[#FFF8EE] transition-colors"
                              onClick={() => setExploreOpen(false)}
                            >
                              <span className="text-[14px] font-bold text-[#111111] hover:text-[#F29A0C]">Pricing</span>
                              <span className="text-[10px] text-[#777] font-semibold mt-0.5">Invest in Transformation</span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/community"
                              className="flex flex-col px-4 py-2.5 rounded-[16px] hover:bg-[#FFF8EE] transition-colors"
                              onClick={() => setExploreOpen(false)}
                            >
                              <span className="text-[14px] font-bold text-[#111111] hover:text-[#F29A0C]">Community</span>
                              <span className="text-[10px] text-[#777] font-semibold mt-0.5">Grow Together In Spirit</span>
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Part 2: Gallery, Milestones, Blog, Contact */}
              {navItems2.map((item, i) => {
                const active = location.pathname === item.path;
                return (
                  <MotionLink key={i} to={item.path} whileHover={{ y:-2 }}
                    className="group relative overflow-hidden px-6 py-3 rounded-full transition-all duration-300">
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400]
                      transition-all duration-300
                      ${active 
                        ? "opacity-100 scale-100 shadow-[0_10px_30px_rgba(242,154,12,0.3)]" 
                        : "opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100"}`}
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-1000
                      bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.25),transparent)]
                      -translate-x-full group-hover:translate-x-full skew-x-12"/>
                    <span className={`relative z-20 text-[16px] font-bold tracking-wide transition-all duration-300
                      ${active 
                        ? "text-white" 
                        : scrolled 
                          ? "text-[#111111] group-hover:text-white" 
                          : "text-white"}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {item.name}
                    </span>
                  </MotionLink>
                );
              })}
            </div>

            {/* DESKTOP ACTIONS */}
            <div className="hidden lg:flex items-center gap-4">

              {auth.loggedIn ? (
                /* ── LOGGED IN ── */
                <div className="flex items-center gap-3" data-avatar-menu>

                  {/* Dashboard shortcut */}
                  <Link to={dashboardPath}>
                    <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                      className={`rounded-full px-6 py-3 text-[15px] font-bold border transition-all duration-300 flex items-center gap-2
                        ${scrolled
                          ? "border-[#F4D9B5] bg-white text-[#111111] hover:border-[#F29A0C] hover:text-[#F29A0C]"
                          : "border-white/15 bg-white/10 text-white backdrop-blur-2xl hover:bg-white/20"}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      <FiGrid size={15}/>
                      Dashboard
                    </motion.button>
                  </Link>

                  {/* Avatar + dropdown */}
                  <div className="relative" data-avatar-menu>
                    <motion.button whileHover={{ scale:1.06 }} whileTap={{ scale:0.94 }}
                      onClick={() => setDropOpen(o => !o)}
                      className="flex items-center gap-2.5 rounded-full pl-1 pr-4 py-1 border transition-all duration-300 cursor-pointer"
                      style={{
                        background: scrolled ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)",
                        borderColor: scrolled ? "#F4D9B5" : "rgba(255,255,255,0.2)",
                        backdropFilter:"blur(16px)",
                      }}>
                      {/* Avatar circle */}
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[12px] font-black overflow-hidden"
                        style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)",
                          fontFamily:"'Poppins',sans-serif", position:"relative" }}>
                        <div className="absolute inset-0" style={{ background:"linear-gradient(135deg,rgba(255,255,255,0.22),transparent)" }}/>
                        <span className="relative z-10">{getInitials(auth.user)}</span>
                      </div>
                      <div className="text-left">
                        <p className={`text-[13px] font-extrabold leading-tight transition-colors duration-300
                          ${scrolled ? "text-[#111]" : "text-white"}`}
                          style={{ fontFamily: "'Inter', sans-serif" }}>
                          {userName.split(" ")[0]}
                        </p>
                        <p className="text-[10px] font-bold" style={{ color:"#F29A0C", fontFamily: "'Inter', sans-serif" }}>{roleLabel}</p>
                      </div>
                      <motion.span animate={{ rotate: dropOpen ? 180 : 0 }} transition={{ duration:0.25 }}
                        className={`text-[10px] ml-1 transition-colors duration-300 ${scrolled ? "text-[#AAA]" : "text-white/60"}`}>
                        ▼
                      </motion.span>
                    </motion.button>

                    {/* Dropdown */}
                    <AnimatePresence>
                      {dropOpen && (
                        <motion.div data-avatar-menu
                          initial={{ opacity:0, y:8, scale:0.96 }}
                          animate={{ opacity:1, y:0, scale:1 }}
                          exit={{ opacity:0, y:8, scale:0.96 }}
                          transition={{ duration:0.22, ease:[0.25,1,0.5,1] }}
                          className="absolute right-0 top-[calc(100%+10px)] w-[220px] rounded-[20px] overflow-hidden z-[9999]"
                          style={{ background:"rgba(255,253,248,0.97)",
                            border:"1px solid rgba(239,229,216,0.8)",
                            boxShadow:"0 20px 60px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)" }}>

                          {/* User info */}
                          <div className="px-4 py-3.5 border-b" style={{ borderColor:"rgba(239,229,216,0.6)" }}>
                            <p className="text-[14px] font-extrabold text-[#1F1F1F]"
                              style={{ fontFamily:"'Poppins',sans-serif" }}>{userName}</p>
                            <p className="text-[12px] mt-0.5 font-medium" style={{ color:"#AAA", fontFamily:"'Inter',sans-serif" }}>
                              {auth.user?.email || ""}
                            </p>
                            <span className="inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                              style={{ background:"rgba(242,154,12,0.1)", color:"#C8660A" }}>
                              {roleLabel}
                            </span>
                          </div>

                          {/* Menu items */}
                          <div className="py-2">
                            <Link to={dashboardPath} onClick={() => setDropOpen(false)}>
                              <motion.div whileHover={{ x:4, background:"rgba(107,138,90,0.06)" }}
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors">
                                <div className="w-7 h-7 rounded-[8px] flex items-center justify-center"
                                  style={{ background:"rgba(107,138,90,0.1)" }}>
                                  <FiGrid size={13} style={{ color:"#6B8A5A" }}/>
                                </div>
                                <span className="text-[14px] font-semibold text-[#1F1F1F]" style={{ fontFamily:"'Inter',sans-serif" }}>
                                  {auth.role === "admin" ? "Admin Panel" : "Dashboard"}
                                </span>
                              </motion.div>
                            </Link>

                            <Link to={auth.role === "admin" ? "/admin/settings" : "/user/settings"}
                              onClick={() => setDropOpen(false)}>
                              <motion.div whileHover={{ x:4, background:"rgba(107,138,90,0.06)" }}
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors">
                                <div className="w-7 h-7 rounded-[8px] flex items-center justify-center"
                                  style={{ background:"rgba(242,154,12,0.1)" }}>
                                  <FiSettings size={13} style={{ color:"#F29A0C" }}/>
                                </div>
                                <span className="text-[14px] font-semibold text-[#1F1F1F]" style={{ fontFamily:"'Inter',sans-serif" }}>Settings</span>
                              </motion.div>
                            </Link>
                          </div>

                          {/* Logout */}
                          <div className="px-3 pb-3 pt-1 border-t" style={{ borderColor:"rgba(239,229,216,0.6)" }}>
                            <motion.button whileHover={{ background:"rgba(226,75,74,0.08)" }}
                              onClick={handleLogout}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] cursor-pointer border-none transition-all"
                              style={{ background:"transparent", fontFamily:"'Inter',sans-serif" }}>
                              <div className="w-7 h-7 rounded-[8px] flex items-center justify-center"
                                style={{ background:"rgba(226,75,74,0.08)" }}>
                                <FiLogOut size={13} style={{ color:"#E24B4A" }}/>
                              </div>
                              <span className="text-[14px] font-bold" style={{ color:"#E24B4A", fontFamily:"'Inter',sans-serif" }}>
                                Logout
                              </span>
                            </motion.button>
                            <p className="text-center text-[10px] text-[#999] font-medium mt-2">
                              Divyyog v{packageJson.version}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

              ) : (
                /* ── NOT LOGGED IN ── */
                <>
                  <Link to="/login">
                    <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }}
                      className={`rounded-full px-6 py-3 text-[15px] font-bold border transition-all duration-300
                        ${scrolled
                          ? "border-[#F4D9B5] bg-white text-[#111111] hover:border-[#F29A0C] hover:text-[#F29A0C]"
                          : "border-white/15 bg-white/10 text-white backdrop-blur-2xl hover:bg-white/20"}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      Login
                    </motion.button>
                  </Link>
                  <Link to="/register">
                    <motion.button whileHover={{ y:-2, scale:1.03 }} whileTap={{ scale:0.97 }}
                      className="btn-shine group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400]
                        px-7 py-3 text-white text-[15px] font-bold shadow-[0_20px_60px_rgba(242,154,12,0.25)] border-none cursor-pointer"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000
                        bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.30),transparent)] skew-x-12"/>
                      <span className="relative z-20 flex items-center gap-3">
                        Register <FaArrowRight size={13}/>
                      </span>
                    </motion.button>
                  </Link>
                </>
              )}
            </div>

            {/* MOBILE HAMBURGER */}
            <button onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300
                ${scrolled ? "bg-[#FFF4E3] text-[#111111]" : "bg-white/10 backdrop-blur-2xl text-white"}`}>
              {mobileOpen ? <FaTimes/> : <FaBars/>}
            </button>

          </div>
        </div>
      </motion.header>

      {/* ══ MOBILE MENU ═════════════════════════════════════════ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Subtle Backdrop Overlay to dismiss on tap */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/45 backdrop-blur-sm z-[997] lg:hidden"
            />

            {/* Mobile Menu Dropdown Floating Container */}
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-[92px] sm:top-[100px] left-3.5 right-3.5 sm:left-4 sm:right-4 z-[998] lg:hidden"
            >
              <div
                className="relative rounded-[26px] border border-[#F4D9B5] bg-white/95 backdrop-blur-3xl p-4 sm:p-5 shadow-[0_25px_80px_rgba(0,0,0,0.18)]
                  max-h-[calc(100dvh-112px)] max-h-[calc(100vh-112px)] overflow-y-auto overscroll-contain
                  [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {/* Background Ambient Glow */}
                <div className="absolute top-[-80px] right-[-80px] w-[200px] h-[200px] rounded-full bg-[#F29A0C]/10 blur-[80px] pointer-events-none" />

                <div className="relative z-20 space-y-2 sm:space-y-2.5">

                  {/* Nav links part 1 (Home, About, Benefits) */}
                  {navItems1.map((item, i) => {
                    const active = location.pathname === item.path;
                    return (
                      <MotionLink
                        key={i}
                        to={item.path}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setMobileOpen(false)}
                        className={`group flex items-center justify-between rounded-xl sm:rounded-2xl border px-4 py-2.5 sm:py-3 transition-all duration-300 ${
                          active
                            ? "border-[#F29A0C] bg-[#FFF4E3] shadow-[0_8px_25px_rgba(242,154,12,0.1)]"
                            : "border-[#F4D9B5]/80 bg-white/80 hover:bg-[#FFF8EE]"
                        }`}
                      >
                        <span
                          className={`text-[15px] sm:text-[16px] font-bold transition-all duration-300 ${
                            active ? "text-[#C8660A]" : "text-[#111111]"
                          }`}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.name}
                        </span>
                        <FaArrowRight className={`text-xs ${active ? "text-[#C8660A]" : "text-[#F29A0C]"}`} />
                      </MotionLink>
                    );
                  })}

                  {/* Mobile Explore Accordion */}
                  <div className="rounded-xl sm:rounded-2xl border border-[#F4D9B5]/80 bg-white/80 overflow-hidden">
                    <button
                      onClick={() => setExploreOpen(!exploreOpen)}
                      className="w-full flex items-center justify-between px-4 py-2.5 sm:py-3 transition-all duration-300 bg-transparent border-none outline-none cursor-pointer"
                    >
                      <span className="text-[15px] sm:text-[16px] font-bold text-[#111111]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Explore
                      </span>
                      <motion.span
                        animate={{ rotate: exploreOpen ? 180 : 0 }}
                        className="text-[#F29A0C] text-[10px]"
                      >
                        ▼
                      </motion.span>
                    </button>

                    <AnimatePresence initial={false}>
                      {exploreOpen && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: "auto" }}
                          exit={{ height: 0 }}
                          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden bg-[#FFFDFB] border-t border-[#F4D9B5]/40"
                        >
                          <div className="p-2 sm:p-2.5 space-y-1 pl-4 sm:pl-5">
                            <Link
                              to="/trainers"
                              onClick={() => { setMobileOpen(false); setExploreOpen(false); }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FFF8EE] text-[#444] font-semibold text-[13.5px] transition-colors"
                            >
                              <span>Trainers & Masters</span>
                              <FaArrowRight size={10} className="text-[#F29A0C]/70" />
                            </Link>
                            <Link
                              to="/courses"
                              onClick={() => { setMobileOpen(false); setExploreOpen(false); }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FFF8EE] text-[#444] font-semibold text-[13.5px] transition-colors"
                            >
                              <span>Courses</span>
                              <FaArrowRight size={10} className="text-[#F29A0C]/70" />
                            </Link>
                            <Link
                              to="/live-classes"
                              onClick={() => { setMobileOpen(false); setExploreOpen(false); }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FFF8EE] text-[#444] font-semibold text-[13.5px] transition-colors"
                            >
                              <span>Live Classes</span>
                              <FaArrowRight size={10} className="text-[#F29A0C]/70" />
                            </Link>
                            <Link
                              to="/pricing"
                              onClick={() => { setMobileOpen(false); setExploreOpen(false); }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FFF8EE] text-[#444] font-semibold text-[13.5px] transition-colors"
                            >
                              <span>Pricing</span>
                              <FaArrowRight size={10} className="text-[#F29A0C]/70" />
                            </Link>
                            <Link
                              to="/community"
                              onClick={() => { setMobileOpen(false); setExploreOpen(false); }}
                              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[#FFF8EE] text-[#444] font-semibold text-[13.5px] transition-colors"
                            >
                              <span>Community</span>
                              <FaArrowRight size={10} className="text-[#F29A0C]/70" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Nav links part 2 (Gallery, Milestones, Blog, Contact) */}
                  {navItems2.map((item, i) => {
                    const active = location.pathname === item.path;
                    return (
                      <MotionLink
                        key={i}
                        to={item.path}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setMobileOpen(false)}
                        className={`group flex items-center justify-between rounded-xl sm:rounded-2xl border px-4 py-2.5 sm:py-3 transition-all duration-300 ${
                          active
                            ? "border-[#F29A0C] bg-[#FFF4E3] shadow-[0_8px_25px_rgba(242,154,12,0.1)]"
                            : "border-[#F4D9B5]/80 bg-white/80 hover:bg-[#FFF8EE]"
                        }`}
                      >
                        <span
                          className={`text-[15px] sm:text-[16px] font-bold transition-all duration-300 ${
                            active ? "text-[#C8660A]" : "text-[#111111]"
                          }`}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.name}
                        </span>
                        <FaArrowRight className={`text-xs ${active ? "text-[#C8660A]" : "text-[#F29A0C]"}`} />
                      </MotionLink>
                    );
                  })}

                  {/* If logged in   show user info + dashboard + logout */}
                  {auth.loggedIn ? (
                    <div className="pt-1.5 space-y-2">
                      {/* User info chip */}
                      <div className="flex items-center gap-3 rounded-xl sm:rounded-2xl border border-[#F4D9B5] bg-white/80 px-4 py-2.5">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[12px] font-black flex-shrink-0"
                          style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", fontFamily: "'Poppins',sans-serif" }}
                        >
                          {getInitials(auth.user)}
                        </div>
                        <div>
                          <p className="text-[13.5px] font-black text-[#111111]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {userName}
                          </p>
                          <p className="text-[11px] font-semibold text-[#F29A0C]" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {roleLabel}
                          </p>
                        </div>
                      </div>

                      {/* Dashboard */}
                      <Link to={dashboardPath} onClick={() => setMobileOpen(false)}>
                        <div className="flex items-center justify-between rounded-xl sm:rounded-2xl border border-[#F4D9B5] bg-white/80 px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <FiGrid size={15} style={{ color: "#6B8A5A" }} />
                            <span className="text-[#111111] text-[14px] font-bold" style={{ fontFamily: "'Inter', sans-serif" }}>
                              {auth.role === "admin" ? "Admin Panel" : "Dashboard"}
                            </span>
                          </div>
                          <FaArrowRight size={12} className="text-[#F29A0C]" />
                        </div>
                      </Link>

                      {/* Logout */}
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={handleLogout}
                        className="w-full flex items-center justify-between rounded-xl sm:rounded-2xl border px-4 py-2.5 cursor-pointer"
                        style={{ borderColor: "rgba(226,75,74,0.25)", background: "rgba(226,75,74,0.05)" }}
                      >
                        <div className="flex items-center gap-2.5">
                          <FiLogOut size={15} style={{ color: "#E24B4A" }} />
                          <span className="font-bold text-[14px]" style={{ color: "#E24B4A", fontFamily: "'Inter', sans-serif" }}>
                            Logout
                          </span>
                        </div>
                      </motion.button>
                    </div>
                  ) : (
                    /* Not logged in   show Login + Register */
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <Link
                        to="/login"
                        onClick={() => setMobileOpen(false)}
                        className="rounded-xl sm:rounded-2xl border border-[#F4D9B5] bg-white py-3 text-[#111111] text-[14px] font-bold text-center transition-all duration-300 hover:border-[#F29A0C] hover:text-[#F29A0C]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Login
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setMobileOpen(false)}
                        className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#F29A0C] to-[#FF8400] py-3 text-white text-[14px] font-bold text-center transition-all duration-300 hover:shadow-[0_15px_45px_rgba(242,154,12,0.25)]"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Register
                      </Link>
                    </div>
                  )}

                  <div className="pt-1.5 text-center">
                    <span className="text-[10.5px] font-semibold text-[#888]">
                      Divyyog Foundation • v{packageJson.version}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;